import * as React from 'react';
import { Sanitized } from '../@types/sanitized';
import { CaptureContext } from '@sentry/types';
import { Coupon } from '../classes/priceSummary/PriceSummary';
import { BillingContext } from './BillingContext';
import { SandboxData } from '../classes/DevInfo/SandboxData';
import { GeoSubscriber, LeadPackageType } from '../helpers/leadsPlusHelpers';
import { GenericContextHandlers } from './GenericContext';
import { FormikProps } from 'formik';
import { BoostPurchaseDetails } from '../@types/boostPurchaseDetails';

export type BoostLeadsContextHandlers = BillingContext &
    Partial<GenericContextHandlers> & {
        contextType: 'leads';
        agent: Sanitized.Agent;
        agentID: string;
        billingDrawerOpen: boolean;
        billingInfoOnFile?: Sanitized.billingInfoOnFileObject;
        cartItem: Sanitized.LeadsCartItem;
        coupon: Coupon;
        couponLoading: boolean;
        couponSavings: string;
        creditCardValueChanged: boolean;
        customerBrandingColors: {};
        customerShortCode: string;
        displayBilling?: boolean;
        frontEndStripeDataFields?: Sanitized.frontEndStripeDataFieldsObject;
        handleChange: (nameValuePairs: Array<Sanitized.CartItemNameValuePair>) => void;
        handleCouponSubmit: (couponCode) => void;
        handleRevertBillingCopyOnly?: (elementID: FormikProps<BoostPurchaseDetails>) => void;
        handleSaveLatest: (formikBag: FormikProps<BoostPurchaseDetails>) => void;
        internalSales: boolean;
        initialValues: BoostPurchaseDetails;
        isBoostLeads: boolean;
        isFormSubmitting: boolean;
        geoCartItems: Sanitized.LeadsGeoCart[];
        loginModal: boolean;
        message: string;
        navigateWithoutHistory: (drawerName: string) => void;
        navigateWithParams: (path: string, params: URLSearchParams, addPath?: boolean) => void;
        packageSize: string;
        platformNetPrice: number;
        platformPrices: Array<Sanitized.platformPriceObject>;
        selectedGeo?: Sanitized.GeoObject;
        selectedGeoSalesMapData?: Array<GeoSubscriber>;
        setBillingDrawerOpen: (billingDrawerOpen: boolean) => void;
        setCreditCardValueChanged: (value: boolean) => void;
        setDisplayBilling?: (elementID: boolean) => void;
        setFormSubmitting: (isFormSubmitting: boolean) => void;
        setMessage: (message: string) => void;
        showFormMobile: boolean;
        startingPoint: Sanitized.LatLng;
        subscriptionManage: boolean;
        throwSentryException: (Error, captureContext?: CaptureContext) => void;
        toggleLoginModal: () => void;
        togglePages?: () => void;
        totalNetPrice: number;
        sandboxData: SandboxData | null;
        boostLeadsSection?: Sanitized.BoostLeadsSection;
        setBoostLeadsSection?: (BoostLeadsSection: Sanitized.BoostLeadsSection) => void;
        savedCreativeValues: BoostPurchaseDetails;
        setLeadTypeView: (type: LeadPackageType) => void;
        leadTypeView: LeadPackageType;
    };

const BoostLeadsContext = React.createContext<Partial<BoostLeadsContextHandlers>>({
    isBoostLeads: false,
});

export const useBoostLeadsContext = (): Partial<BoostLeadsContextHandlers> => {
    return React.useContext(BoostLeadsContext);
};

export default BoostLeadsContext;
